/**
 * Исправления приходов — вкладка «Накладных» (O15, D268).
 *
 * Агент сверил накладные StoreHouse с фото бумаги и подготовил правки.
 * Здесь по каждой накладной — «было → станет», почему, и фото бумаги рядом.
 * Миниатюра фото открывает под карточкой тот же просмотрщик, что у
 * накладной: страницы, масштаб и поворот с сохранением (владелец, 26.10.2026).
 * «Подтвердить» отправляет все правки накладной в StoreHouse; агент
 * записывает их, перечитывает документ и показывает, совпало ли.
 * Подтверждать может только тот, у кого право «Накладные · исправлять в
 * StoreHouse»; остальные видят список без кнопок.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@shell/api";
import { Empty, ErrorState, Loading } from "@shell/states";
import { photoObjectUrl, portalApi, qty, rub } from "../../api";
import type {
  Correction,
  CorrectionEdit,
  CorrectionGroup,
  CorrectionList,
  CorrectionSide,
} from "../../api";
import { Icon } from "../../icons";
import { Pill, isoDate } from "../../ui";
import { PhotoViewer } from "./PhotoViewer";

const GROUPS: { code: CorrectionGroup; title: string; empty: string }[] = [
  {
    code: "open",
    title: "Ждут решения",
    empty: "Неподтверждённых исправлений нет.",
  },
  {
    code: "progress",
    title: "Записываются",
    empty: "Сейчас в StoreHouse ничего не пишется.",
  },
  {
    code: "closed",
    title: "Готово и ошибки",
    empty: "Пока ничего не записано.",
  },
];

const STATUS: Record<Correction["status"], { text: string; colour: string }> = {
  prepared: { text: "ждёт решения", colour: "yellow" },
  confirmed: { text: "подтверждено, ждёт записи", colour: "yellow" },
  writing: { text: "записывается", colour: "yellow" },
  done: { text: "записано и сверено", colour: "green" },
  failed: { text: "не записалось", colour: "red" },
  stale: { text: "документ поменяли — не записано", colour: "grey" },
  rejected: { text: "отклонено", colour: "grey" },
};

export function correctionsKey(group: CorrectionGroup) {
  return ["receipts", "corrections", group] as const;
}

export function Corrections() {
  const [group, setGroup] = useState<CorrectionGroup>("open");
  const query = useQuery<CorrectionList, ApiError>({
    queryKey: correctionsKey(group),
    queryFn: () => portalApi.corrections(group),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });
  const counts = query.data?.counts;

  return (
    <section className="corrections" aria-label="Исправления накладных">
      <div className="corrections__head">
        <h1 className="queue__title">Исправления в StoreHouse</h1>
        <p className="corrections__lead">
          Накладные, которые заведены в StoreHouse не так, как на бумаге.
          Проверьте по фото и подтвердите — исправление запишется в StoreHouse,
          накладная останется проведённой.
        </p>
      </div>
      <div className="chips" role="tablist" aria-label="Исправления">
        {GROUPS.map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={item.code === group}
            className={`chip${item.code === group ? " chip--active" : ""}`}
            onClick={() => setGroup(item.code)}
          >
            {item.title}
            {counts && (
              <span className="chip__count">{counts[item.code] ?? 0}</span>
            )}
          </button>
        ))}
      </div>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <Empty
          title="Пусто"
          text={GROUPS.find((item) => item.code === group)?.empty ?? ""}
        />
      ) : (
        <ul className="corrections__list">
          {query.data.items.map((item) => (
            <CorrectionCard
              key={item.id}
              item={item}
              mayConfirm={query.data.may_confirm}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function CorrectionCard({
  item,
  mayConfirm,
}: {
  item: Correction;
  mayConfirm: boolean;
}) {
  const client = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [viewing, setViewing] = useState(false);
  const refresh = () =>
    client.invalidateQueries({ queryKey: ["receipts", "corrections"] });
  const confirm = useMutation<Correction, ApiError>({
    mutationFn: () => portalApi.confirmCorrection(item.id),
    onSuccess: refresh,
  });
  const reject = useMutation<Correction, ApiError>({
    mutationFn: () => portalApi.rejectCorrection(item.id, reason),
    onSuccess: refresh,
  });
  const status = STATUS[item.status];
  const error = confirm.error ?? reject.error;
  const photos = Array.from(new Set(item.edits.flatMap((edit) => edit.photos)));
  const pages = item.pages ?? [];
  const receiptId = item.document.receipt_id;

  return (
    <li className="correction">
      <div className="correction__head">
        <div>
          <strong>{item.document.supplier ?? "Поставщик"}</strong> · №{" "}
          {item.document.number ?? "—"} от {isoDate(item.document.date)}
          {item.document.posted && (
            <span className="correction__posted"> · проведена</span>
          )}
        </div>
        <Pill colour={status.colour}>{status.text}</Pill>
      </div>
      {item.note && <p className="correction__note">{item.note}</p>}
      <table className="correction__table">
        <thead>
          <tr>
            <th>Что</th>
            <th>Было в StoreHouse</th>
            <th>Станет</th>
            <th>Почему</th>
          </tr>
        </thead>
        <tbody>
          {item.edits.map((edit, index) => (
            <EditRow key={index} edit={edit} />
          ))}
        </tbody>
      </table>
      {photos.length > 0 && (
        <div className="correction__photos">
          {photos.map((url) => (
            <Photo
              key={url}
              url={url}
              onOpen={
                pages.length > 0 && receiptId
                  ? () => setViewing(!viewing)
                  : undefined
              }
            />
          ))}
          {pages.length > 0 && receiptId && (
            <button
              type="button"
              className="button button--quiet"
              aria-expanded={viewing}
              onClick={() => setViewing(!viewing)}
            >
              <Icon name="rotate" size={16} />{" "}
              {viewing ? "Скрыть фото" : "Открыть фото: повернуть, увеличить"}
            </button>
          )}
        </div>
      )}
      {viewing && receiptId && pages.length > 0 && (
        <div className="correction__viewer">
          <PhotoViewer receiptId={receiptId} pages={pages} />
        </div>
      )}
      {item.failure && <p className="correction__failure">{item.failure}</p>}
      {item.reject_reason && (
        <p className="correction__note">Отклонено: {item.reject_reason}</p>
      )}
      {error && (
        <p className="correction__failure">{error.detail ?? error.message}</p>
      )}
      {item.status === "prepared" && mayConfirm && (
        <div className="correction__actions">
          {rejecting ? (
            <>
              <input
                className="correction__reason"
                value={reason}
                placeholder="Почему не нужно"
                onChange={(event) => setReason(event.target.value)}
                aria-label="Причина отказа"
              />
              <button
                type="button"
                className="button button--quiet"
                disabled={!reason.trim() || reject.isPending}
                onClick={() => reject.mutate()}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => setRejecting(false)}
              >
                Отмена
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="button button--primary"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate()}
              >
                <Icon name="check" size={16} /> Подтвердить и записать в
                StoreHouse
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => setRejecting(true)}
              >
                Отклонить
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function side(value: CorrectionSide | null): string {
  if (!value) return "—";
  const parts = [`${value.goods}`, `${qty(value.quantity)} ${value.unit}`];
  const sum = rub(value.net);
  if (sum) parts.push(`${sum} без НДС`);
  if (value.vat !== null)
    parts.push(`НДС ${rub(value.vat)} (${value.vat_rate}%)`);
  const price = rub(value.price);
  if (price) parts.push(`${price} за ${value.unit}`);
  return parts.join(" · ");
}

function EditRow({ edit }: { edit: CorrectionEdit }) {
  return (
    <tr>
      <td>{edit.title}</td>
      <td>{side(edit.before)}</td>
      <td>{side(edit.after)}</td>
      <td>
        {edit.reason}
        {edit.paper && (
          <div className="correction__paper">На бумаге: {edit.paper}</div>
        )}
      </td>
    </tr>
  );
}

function Photo({ url, onOpen }: { url: string; onOpen?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    photoObjectUrl(url)
      .then((value) => alive && setSrc(value))
      .catch(() => alive && setSrc(null));
    return () => {
      alive = false;
    };
  }, [url]);
  if (!src)
    return (
      <span className="correction__photo correction__photo--empty">фото…</span>
    );
  if (onOpen) {
    return (
      <button
        type="button"
        className="correction__photo"
        onClick={onOpen}
        aria-label="Открыть фото накладной"
      >
        <img src={src} alt="Фото накладной" />
      </button>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="correction__photo"
    >
      <img src={src} alt="Фото накладной" />
    </a>
  );
}
